import { lazy } from "react";
import { ProductObj } from "../apps/Bundles/Types";

export function getFieldError(errors: any, path: string): string | undefined {
    const message = path.split(".").reduce((node, key) => (node == null ? undefined : node[key]), errors)?.message;
    return typeof message === "string" ? message : undefined;
}

export function getBoolean(str?: string | undefined | boolean) {
    if (typeof str == "string") {
        if (isNaN(parseInt(str))) {
            if (str?.toLowerCase() == "true") {
                return true;
            } else {
                return false;
            }
        } else {
            return !!parseInt(str);
        }
    } else {
        return str;
    }
}

export const isValidUrl = (urlString: string = "") => {
    if (!urlString) {
        return false;
    }
    const urlPattern = new RegExp(
        "^(http(s)?:\\/\\/)?" + // validate protocol
            "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
            "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
            "(\\:\\d+)?(\\/[-a-z\\d%_.,~+]*)*" + // validate port and path
            "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
            "(\\#[-a-z\\d_]*)?$",
        "i",
    );
    return !!urlPattern.test(urlString);
};

export function isEqual(value: any, other: any, excludeKeys: string[] = []): boolean {
    // Helper function to remove excluded keys from an object
    const removeExcludedKeys = (obj: any, keys: string[]) => {
        if (typeof obj !== "object" || obj === null) {
            return obj;
        }
        const result = Array.isArray(obj) ? [...obj] : { ...obj };
        for (const key of keys) {
            if (key in result) {
                delete result[key];
            }
        }
        for (const key in result) {
            result[key] = removeExcludedKeys(result[key], keys);
        }
        return result;
    };

    // Remove excluded keys from both objects
    value = removeExcludedKeys(value, excludeKeys);
    other = removeExcludedKeys(other, excludeKeys);

    // Helper function to check equality of two objects
    const areObjectsEqual = (obj1: any, obj2: any): boolean => {
        const obj1Keys = Object.keys(obj1);
        const obj2Keys = Object.keys(obj2);

        if (obj1Keys.length !== obj2Keys.length) {
            return false;
        }

        for (const key of obj1Keys) {
            if (!obj2Keys.includes(key)) {
                return false;
            }
            if (!isEqual(obj1[key], obj2[key], excludeKeys)) {
                console._log(
                    `Different values for key '${key}': obj1 = ${JSON.stringify(obj1[key])}, obj2 = ${JSON.stringify(
                        obj2[key],
                    )}`,
                );
                return false;
            }
        }

        return true;
    };

    // Check if both values are strictly equal
    if (value === other) {
        return true;
    }

    // Check for null or undefined values
    if (value == null || other == null) {
        return false;
    }

    // Check if both values are objects
    if (typeof value === "object" && typeof other === "object") {
        return areObjectsEqual(value, other);
    }

    // Handle primitive types and non-object types
    return false;
}
export const parseUrl = (url: string = "") => {
    if (!url) {
        return url;
    }
    if (url.includes("https://")) return url;
    else url = "https://" + url;

    return url;
};

export const arrayToCSV = (data: any) => {
    const csvRows = [];

    // Get the headers (object keys)
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    // Loop through the array and convert each object to a CSV row
    for (const row of data) {
        const values = headers.map((header) => JSON.stringify(row[header], replacer));
        csvRows.push(values.join(","));
    }
    return csvRows.join("\n");
};

// Optional replacer function for handling special cases
const replacer = (key: string, value: any) => {
    return value === null ? "" : value;
};

// utils.ts
export const sanitizeValue = (value: number | undefined): string => {
    if (value === undefined || value === null) return "";

    const sanitizedValue = value.toString();

    // Remove leading zeros
    // sanitizedValue = sanitizedValue.replace(/^0+(?=\d)/, "");

    // Ensure non-negative for other types
    return sanitizedValue;
};

export function getCookieValue(cookieName: string) {
    const cookies = document.cookie.split("; ");
    for (const cookie of cookies) {
        const [name, value] = cookie.split("=");
        if (name === cookieName) {
            return decodeURIComponent(value);
        }
    }
    return null; // Return null if the cookie is not found
}

export const lazyWithErrorHandling = (importFn: () => Promise<any>) => {
    return lazy(() => {
        return importFn().catch((error) => {
            console.error("Lazy load failed:", error);
            window.location.reload(); // Reload the page on chunk load failure
            return Promise.reject(error); // Prevent further rendering after error
        });
    });
};

export const initDeferredScripts = () => {
    let executed = false;
    const run = () => {
        if (executed) return;
        executed = true;

        window.zipy && window.zipy.init("9a34eafb", { recordOnlyIframe: true });

        window.zipy && window.zipy.identify(shopify.config.shop, { firstName: shopify.config.shop });
    };

    window.addEventListener("load", run);

    if (document.readyState === "complete") {
        run();
    }
};

export const scrollToEnd = () => {
    window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "auto",
    });
};

export const scrollToTop = () => {
    const modalScrollEl = document.querySelector(".Polaris-Modal__Body.Polaris-Scrollable");
    // for ai bundle modal, we are doing it
    if (modalScrollEl) {
        modalScrollEl.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }
    window.scrollTo({
        top: 0,
        behavior: "auto", // or "smooth"
    });
};

export const truncateFileName = (name: string, maxLength: number) => {
    if (name?.length <= maxLength) return name;
    const extension = name.split(".").pop();
    const truncatedName = name.slice(0, maxLength - (extension?.length || 0) - 3); // Truncate and reserve space for '...'
    return `${truncatedName}...${extension}`;
};

export function mergeProductsById(products: ProductObj[]): ProductObj[] {
    const merged: Record<string, ProductObj> = {};

    for (const product of products) {
        const { id } = product;

        if (!merged[id]) {
            merged[id] = { ...product, variants: [...(product.variants || [])] };
        } else {
            merged[id].variants?.push(...(product.variants || []));
        }
    }

    return Object.values(merged);
}

export function flattenToVariants(products: ProductObj[]): ProductObj[] {
    const expanded = [];

    for (const product of products) {
        for (const variant of product.variants || []) {
            expanded.push({
                ...product,
                selectedVariants: [variant],
                variants: [variant],
                title: product.title || variant.title,
            });
        }
    }

    return expanded;
}
